import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "../context/AuthContext";
import {
  LayoutDashboard, ClipboardList, BookOpen, FileText,
  Activity, Users, LogOut, Receipt, Boxes, ChefHat, Menu, X, ClipboardCheck,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import BrandMark from "./BrandMark";

const ADMIN_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/kitchen", label: "Inventory", icon: Boxes },
  { to: "/orders", label: "Orders", icon: ClipboardList },
  { to: "/catalog", label: "Catalog", icon: BookOpen },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/team", label: "Team", icon: Users },
];

const FRANCHISEE_NAV = [
  { to: "/orders?tab=mine", label: "Orders", icon: ClipboardList },
  { to: "/my-inventory", label: "My Inventory", icon: Boxes },
  { to: "/daily-report", label: "End-of-Day Count", icon: ClipboardCheck },
  { to: "/invoices", label: "Invoices", icon: Receipt },
  { to: "/activity", label: "Activity", icon: Activity },
];

const WAREHOUSE_EXTRA_NAV = [
  { to: "/orders?tab=fulfill", label: "Fulfillment", icon: ChefHat },
  { to: "/kitchen", label: "Warehouse Inventory", icon: Boxes },
];

const EMPLOYEE_NAV = [
  { to: "/kitchen", label: "Kitchen Quick Log", icon: ChefHat },
  { to: "/my-orders", label: "My Orders", icon: ClipboardList },
  { to: "/activity", label: "Activity", icon: Activity },
];

function SidebarContent({ onNavigate, showBell = true }) {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const items = user?.role === "admin"
    ? ADMIN_NAV
    : user?.role === "franchisee"
      ? (user.warehouse ? [...FRANCHISEE_NAV.slice(0, 1), ...WAREHOUSE_EXTRA_NAV, ...FRANCHISEE_NAV.slice(2)] : FRANCHISEE_NAV)
      : EMPLOYEE_NAV;

  return (
    <>
      <div className="px-6 pt-8 pb-6 flex items-start justify-between gap-2">
        <BrandMark size="sm" data-testid="brand-sidebar" />
        {showBell && <NotificationBell variant="sidebar" />}
      </div>

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/" || n.to === "/kitchen"}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                (n.to.includes("?") ? `${loc.pathname}${loc.search}`.startsWith(n.to)
                  : isActive && !(n.to === "/orders?tab=mine" && loc.search.includes("tab=fulfill")))
                  ? "bg-white text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-border"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/60"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <span className="font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-4">
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white border border-border">
          <div className="h-9 w-9 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{ROLE_LABELS[user?.role]}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

export default function Sidebar({ mobileOpen = false, onClose }) {
  return (
    <>
      {/* Desktop — always visible from md up, identical to before */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-[hsl(var(--sidebar))]"
      >
        <SidebarContent />
      </aside>

      {/* Mobile — slide-in drawer, controlled by hamburger in MobileTopBar */}
      <div
        className={`md:hidden fixed inset-0 z-[70] transition-opacity ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        {/* Backdrop */}
        <div
          onClick={onClose}
          data-testid="mobile-sidebar-backdrop"
          className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        />
        {/* Drawer */}
        <aside
          data-testid="mobile-sidebar-drawer"
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[hsl(var(--sidebar))] border-r border-border flex flex-col shadow-[var(--shadow-floating)] transition-transform ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button
            onClick={onClose}
            data-testid="mobile-sidebar-close"
            aria-label="Close menu"
            className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
          {/* Hide the redundant sidebar bell in the mobile drawer — the
              MobileTopBar already has its own bell, and its pulse-dot
              overlaps the close X when both render together. */}
          <SidebarContent onNavigate={onClose} showBell={false} />
        </aside>
      </div>
    </>
  );
}

export function MobileTopBar({ onOpenMenu }) {
  return (
    <div className="md:hidden border-b border-border bg-[hsl(var(--sidebar))] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button
          onClick={onOpenMenu}
          data-testid="mobile-menu-btn"
          aria-label="Open navigation menu"
          className="h-10 w-10 rounded-full border border-border bg-card flex items-center justify-center text-foreground hover:bg-secondary active:scale-95 transition-all"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <BrandMark size="sm" />
      </div>
      <NotificationBell variant="mobile" />
    </div>
  );
}
